/** 与 `checkSeedanceConnection` 成功响应结构一致，避免 api/seedance 循环依赖 */
export type CachedTokenUsageResponse = {
  code: boolean;
  data?: {
    expires_at?: number;
    name?: string;
    [key: string]: unknown;
  };
  message?: string;
};

/** 同一 API Key 校验成功后，在此时间内不再请求 GET /api/usage/token/ */
export const TOKEN_USAGE_CHECK_CACHE_MS = 10 * 60 * 1000;

const STORAGE_KEY = 'ss_token_usage_check_cache';

type CacheEntry = {
  validatedAt: number;
  response: CachedTokenUsageResponse;
};

type CacheStore = Record<string, CacheEntry>;

const memoryCache = new Map<string, CacheEntry>();

function cleanApiKey(apiKey: string): string {
  return apiKey.trim().replace(/^Bearer\s+/i, '').replace(/[^a-zA-Z0-9-]/g, '');
}

function readSessionStore(): CacheStore {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as CacheStore;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionStore(store: CacheStore) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

function isFresh(entry: CacheEntry | undefined): entry is CacheEntry {
  if (!entry) return false;
  return Date.now() - entry.validatedAt < TOKEN_USAGE_CHECK_CACHE_MS;
}

function getEntry(apiKey: string): CacheEntry | undefined {
  const key = cleanApiKey(apiKey);
  if (!key) return undefined;

  const fromMemory = memoryCache.get(key);
  if (isFresh(fromMemory)) return fromMemory;
  if (fromMemory) memoryCache.delete(key);

  const store = readSessionStore();
  const fromSession = store[key];
  if (isFresh(fromSession)) {
    memoryCache.set(key, fromSession);
    return fromSession;
  }
  if (fromSession) {
    delete store[key];
    writeSessionStore(store);
  }
  return undefined;
}

/** 读取缓存的成功校验结果；未命中或已过期返回 null。 */
export function getCachedTokenUsageCheck(apiKey: string): CachedTokenUsageResponse | null {
  const entry = getEntry(apiKey);
  if (!entry || entry.response.code !== true) return null;
  return entry.response;
}

export function setCachedTokenUsageCheck(apiKey: string, response: CachedTokenUsageResponse) {
  const key = cleanApiKey(apiKey);
  if (!key || response.code !== true) return;

  const entry: CacheEntry = { validatedAt: Date.now(), response };
  memoryCache.set(key, entry);

  const store = readSessionStore();
  store[key] = entry;
  writeSessionStore(store);
}

export function invalidateTokenUsageCheckCache(apiKey?: string) {
  if (!apiKey) {
    memoryCache.clear();
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
    return;
  }

  const key = cleanApiKey(apiKey);
  if (!key) return;
  memoryCache.delete(key);

  const store = readSessionStore();
  if (store[key]) {
    delete store[key];
    writeSessionStore(store);
  }
}
